var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Children, forwardRef, useEffect, useMemo, useRef, useState, } from "react";
import TabsList from "./TabsList";
import TabPanel from "./TabPanel";
import { TabContext } from "./Tab.context";
import { useMergeRefs } from "../../../common/useFallbackRef";
var TabsV2 = forwardRef(function (_a, ref) {
    var defaultSelected = _a.defaultSelected, onChangeTab = _a.onChangeTab, children = _a.children, className = _a.className, style = _a.style, otherProps = __rest(_a, ["defaultSelected", "onChangeTab", "children", "className", "style"]);
    var _b = useMemo(function () {
        var tabsListElement = undefined;
        var tabPanelElements = [];
        var idTabsPanels = [];
        Children.forEach(children, function (child) {
            if (React.isValidElement(child)) {
                var idTab = child.props.idTab;
                switch (child.type) {
                    case TabsList:
                        tabsListElement = child;
                        break;
                    case TabPanel:
                        idTabsPanels.push(idTab);
                        tabPanelElements.push(child);
                        break;
                }
            }
        });
        return [tabsListElement, tabPanelElements, idTabsPanels];
    }, [children]), tabsList = _b[0], tabPanels = _b[1], idTabs = _b[2];
    var _c = useState(defaultSelected !== null && defaultSelected !== void 0 ? defaultSelected : idTabs[0]), selected = _c[0], setSelected = _c[1];
    /**
     * Ref
     */
    var tabListRef = useRef(null);
    var internalRef = useRef(null);
    var combinedRef = useMergeRefs([ref, internalRef]);
    /**
     * Focus
     */
    var _d = useState(null), elementToFocus = _d[0], setElementToFocus = _d[1];
    useEffect(function () {
        if (elementToFocus) {
            var interval_1 = setInterval(function () {
                elementToFocus.focus();
                if (document.activeElement === elementToFocus) {
                    setElementToFocus(null);
                    clearInterval(interval_1);
                }
            }, 10);
            return function () {
                clearInterval(interval_1);
            };
        }
        return;
    }, [elementToFocus]);
    var handleKeyDown = function (e) {
        if (!tabListRef.current)
            return;
        switch (e.key) {
            case "ArrowLeft":
                e.preventDefault();
                focusPreviousTab();
                break;
            case "ArrowRight":
                e.preventDefault();
                focusNextTab();
                break;
            case "Home":
                e.preventDefault();
                focusFirstTab();
                break;
            case "End":
                e.preventDefault();
                focusLastTab();
                break;
            case "Tab":
                if (e.shiftKey) {
                    moveToPreviousFocusableElement();
                }
                else {
                    moveToNextFocusableElement();
                }
                break;
            default:
                break;
        }
    };
    var focusPreviousTab = function () {
        var positionOfSelected = idTabs === null || idTabs === void 0 ? void 0 : idTabs.findIndex(function (element) { return element === selected; });
        if (positionOfSelected === undefined) {
            return;
        }
        var newPosition = positionOfSelected - 1;
        if (newPosition < 0) {
            newPosition = idTabs.length - 1;
        }
        handleClickTab(idTabs[newPosition]);
        setElementToFocus(document.getElementById(idTabs[newPosition]));
    };
    var focusNextTab = function () {
        var positionOfSelected = idTabs === null || idTabs === void 0 ? void 0 : idTabs.findIndex(function (element) { return element === selected; });
        if (positionOfSelected === undefined) {
            return;
        }
        var newPosition = positionOfSelected + 1;
        if (newPosition >= idTabs.length) {
            newPosition = 0;
        }
        handleClickTab(idTabs[newPosition]);
        setElementToFocus(document.getElementById(idTabs[newPosition]));
    };
    var focusFirstTab = function () {
        if (!idTabs || idTabs.length === 0) {
            return;
        }
        var firstTabId = idTabs[0];
        handleClickTab(firstTabId);
        setElementToFocus(document.getElementById(firstTabId));
    };
    var focusLastTab = function () {
        if (!idTabs || idTabs.length === 0) {
            return;
        }
        var lastTabId = idTabs[idTabs.length - 1];
        handleClickTab(lastTabId);
        setElementToFocus(document.getElementById(lastTabId));
    };
    var moveToNextFocusableElement = function () {
        var focusableElements = Array.from(document.querySelectorAll("a, button, input, textarea, select, [tabindex]"));
        var currentIndex = focusableElements.findIndex(function (element) { return element === document.activeElement; });
        for (var i = currentIndex + 1; i < focusableElements.length; i++) {
            var element = focusableElements[i];
            if (element.tabIndex >= 0 && !element.hasAttribute("disabled")) {
                setElementToFocus(element);
                break;
            }
        }
    };
    var moveToPreviousFocusableElement = function () {
        var focusableElements = Array.from(document.querySelectorAll("a, button, input, textarea, select, [tabindex]"));
        var currentIndex = focusableElements.findIndex(function (element) { return element === document.activeElement; });
        for (var i = currentIndex - 1; i > 0; i--) {
            var element = focusableElements[i];
            if (element.tabIndex >= 0 && !element.hasAttribute("disabled")) {
                setElementToFocus(element);
                break;
            }
        }
    };
    useEffect(function () {
        if (selected && tabListRef.current) {
            var activeTabElement = tabListRef.current.querySelector("[role='tab'][id='".concat(selected, "']"));
            if (activeTabElement) {
                activeTabElement.focus();
            }
        }
    }, [selected]);
    var scroll = function () {
        if (!tabListRef.current) {
            return;
        }
        var currentList = tabListRef.current;
        var scrollLeft = currentList.scrollLeft;
        var isMin = scrollLeft <= 16;
        var max = currentList.scrollWidth - currentList.clientWidth - 16;
        var isMax = Math.abs(scrollLeft) >= max;
        var isRtl = document.documentElement.getAttribute("dir") === "rtl";
        var minSelector = isRtl ? "fr-tabs__shadow--right" : "fr-tabs__shadow--left";
        var maxSelector = isRtl ? "fr-tabs__shadow--left" : "fr-tabs__shadow--right";
        if (isMin) {
            currentList.classList.remove(minSelector);
        }
        else {
            currentList.classList.add(minSelector);
        }
        if (isMax) {
            currentList.classList.remove(maxSelector);
        }
        else {
            currentList.classList.add(maxSelector);
        }
    };
    useEffect(function () {
        var handleResize = function () {
            if (!internalRef.current || !tabListRef.current) {
                return;
            }
            var current = internalRef.current;
            var currentList = tabListRef.current;
            var selectedPanel = current.getElementsByClassName("fr-tabs__panel--selected")[0];
            if (!selectedPanel) {
                return;
            }
            current.style.setProperty("--tabs-height", "auto");
            current.scrollTop = 0;
            current.scrollLeft = 0;
            var heightOngletActif = selectedPanel.offsetHeight;
            var heightList = currentList.offsetHeight;
            current.style.setProperty("--tabs-height", (heightOngletActif + heightList).toString() + "px");
            var rect = currentList.getBoundingClientRect();
            currentList.style.setProperty("--tabs-list-height", "".concat(rect.height, "px"));
            scroll();
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        var resizeObserver = new ResizeObserver(function () {
            handleResize();
        });
        if (internalRef.current) {
            var current = internalRef.current;
            if (current.getElementsByClassName("fr-tabs__panel--selected")[0]) {
                resizeObserver.observe(current.getElementsByClassName("fr-tabs__panel--selected")[0]);
            }
        }
        return function () {
            resizeObserver.disconnect();
            window.removeEventListener("resize", handleResize);
        };
    }, [children, selected, scroll]);
    var handleClickTab = function (id) {
        if (onChangeTab) {
            onChangeTab(id);
        }
        setSelected(id);
    };
    return (React.createElement("div", __assign({}, otherProps, { className: className ? "fr-tabs ".concat(className) : "fr-tabs", style: style, role: "tablist", ref: combinedRef }),
        React.createElement(TabContext.Provider, { value: {
                idTabs: idTabs,
                tabListRef: tabListRef,
                scroll: scroll,
                onChange: handleClickTab,
                selected: selected,
                handleKeyDown: handleKeyDown,
            } },
            tabsList,
            tabPanels)));
});
export default TabsV2;
